import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";
import { MessageCircle, X, Send, Loader2, Sparkles, Headset } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/chat`;
const STORE = "masaya_chat_v1";
// Hide the concierge on portal / management / transactional routes — it's a public-site helper.
const HIDE_PREFIXES = ["/portal", "/management", "/homeowner", "/guest", "/pay"];

const load = () => { try { return JSON.parse(localStorage.getItem(STORE) || "null"); } catch { return null; } };
const save = (v) => localStorage.setItem(STORE, JSON.stringify(v));

export default function ChatWidget() {
  const { pathname } = useLocation();
  const hidden = HIDE_PREFIXES.some((p) => pathname.startsWith(p));
  const [open, setOpen] = useState(false);
  const [convo, setConvo] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [booting, setBooting] = useState(false);
  const [settings, setSettings] = useState({ management_online: true });
  const [reqForm, setReqForm] = useState(null); // {name,email,note} when requesting management
  const [convoId, setConvoId] = useState(() => load()?.id || null);
  const stored = useRef(load());
  const endRef = useRef(null);

  // Resume via secure link (?chat=&ct=) if present.
  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    const cid = p.get("chat"), ct = p.get("ct");
    if (cid && ct) { stored.current = { id: cid, token: ct }; save(stored.current); setConvoId(cid); setOpen(true); }
  }, []);

  useEffect(() => {
    axios.get(`${API}/settings`).then(({ data }) => setSettings(data)).catch(() => {});
  }, []);

  const applyState = useCallback((data) => {
    setConvo(data);
    setMessages(data.messages || []);
    if (data.management_online !== undefined) setSettings((s) => ({ ...s, management_online: data.management_online }));
  }, []);

  const ensureStarted = useCallback(async () => {
    if (stored.current?.id) return stored.current;
    setBooting(true);
    try {
      const { data } = await axios.post(`${API}/start`, {});
      stored.current = { id: data.id, token: data.session_token };
      save(stored.current);
      setConvoId(data.id);
      applyState(data);
      return stored.current;
    } finally { setBooting(false); }
  }, [applyState]);

  // Poll while open. Keyed on convoId (state) so the interval starts as soon as a conversation
  // exists — including brand-new conversations created within this session.
  useEffect(() => {
    if (!open || !convoId || !stored.current?.id) return;
    let active = true;
    const tick = async () => {
      try {
        const { data } = await axios.get(`${API}/${stored.current.id}`, { headers: { "X-Chat-Token": stored.current.token } });
        if (active) applyState(data);
      } catch { /* conversation expired/purged → reset */ if (active) { localStorage.removeItem(STORE); stored.current = null; setConvoId(null); setConvo(null); setMessages([]); } }
    };
    tick();
    const t = setInterval(tick, 3000);
    return () => { active = false; clearInterval(t); };
  }, [open, convoId, applyState]);

  useEffect(() => { if (open) ensureStarted(); }, [open, ensureStarted]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, open]);

  const send = async () => {
    const text = input.trim();
    if (!text || sending) return;
    setSending(true);
    const s = await ensureStarted();
    setMessages((m) => [...m, { id: "tmp", role: "guest", author: "You", text }]);
    setInput("");
    try {
      const { data } = await axios.post(`${API}/${s.id}/message`, { text }, { headers: { "X-Chat-Token": s.token } });
      applyState(data);
    } catch { /* keep optimistic; next poll reconciles */ } finally { setSending(false); }
  };

  const submitHuman = async () => {
    const s = await ensureStarted();
    setSending(true);
    try {
      const { data } = await axios.post(`${API}/${s.id}/request-human`,
        { name: reqForm?.name || null, email: reqForm?.email || null, note: null }, { headers: { "X-Chat-Token": s.token } });
      applyState(data);
      setReqForm(null);
    } catch { /* ignore */ } finally { setSending(false); }
  };

  if (hidden) return null;

  const humanMode = convo?.mode === "human";

  return (
    <>
      {!open && (
        <button data-testid="chat-widget-button" onClick={() => setOpen(true)} aria-label="Open Masaya concierge chat"
          className="fixed bottom-5 right-5 z-[60] flex items-center gap-2 rounded-full bg-[#1C1917] text-white pl-4 pr-5 py-3 shadow-xl hover:bg-[#44403C] transition-colors duration-300">
          <MessageCircle className="w-5 h-5" strokeWidth={1.75} />
          <span className="text-sm font-medium">Chat with us</span>
        </button>
      )}

      {open && (
        <div data-testid="chat-widget-panel" className="fixed bottom-5 right-5 z-[60] w-[min(92vw,380px)] h-[min(78vh,560px)] flex flex-col rounded-2xl bg-white border border-[#E7E5E4] shadow-2xl overflow-hidden">
          {/* header */}
          <div className="flex items-center justify-between gap-2 px-4 py-3 bg-[#1C1917] text-white">
            <div className="flex items-center gap-2.5">
              <span className={`w-8 h-8 rounded-full flex items-center justify-center ${humanMode ? "bg-[#8A9A86]" : "bg-[#44403C]"}`}>
                {humanMode ? <Headset className="w-4 h-4" strokeWidth={1.75} /> : <Sparkles className="w-4 h-4" strokeWidth={1.75} />}
              </span>
              <div>
                <p className="text-sm font-medium leading-tight" data-testid="chat-mode-label">{humanMode ? "Masaya Management" : "Masaya AI Concierge"}</p>
                <p className="text-[11px] text-[#D6D3D1] leading-tight">{humanMode ? "You're chatting with our team" : "AI-powered · ask anything"}</p>
              </div>
            </div>
            <button data-testid="chat-widget-close" onClick={() => setOpen(false)} aria-label="Close chat" className="p-1.5 text-[#D6D3D1] hover:text-white"><X className="w-5 h-5" strokeWidth={1.75} /></button>
          </div>

          {/* messages */}
          <div data-testid="chat-messages" className="flex-1 overflow-y-auto px-3.5 py-4 space-y-3 bg-[#FAFAF9]">
            {booting && <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-[#8A9A86]" /></div>}
            {messages.map((m, i) => <Bubble key={m.id === "tmp" ? `tmp-${i}` : m.id} m={m} />)}
            <div ref={endRef} />
          </div>

          {/* request-management form */}
          {reqForm !== null && (
            <div className="px-3.5 py-3 border-t border-[#E7E5E4] bg-white space-y-2" data-testid="chat-request-form">
              {!settings.management_online && (
                <p className="text-[11px] text-[#92400E] bg-[#FEF3C7] rounded-lg px-2.5 py-1.5">Our team is offline right now. Leave your email and we'll reply by email too.</p>
              )}
              <input data-testid="chat-req-name" value={reqForm.name || ""} onChange={(e) => setReqForm({ ...reqForm, name: e.target.value })} placeholder="Your name (optional)" className="w-full rounded-lg border border-[#E7E5E4] px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[#8A9A86]" />
              <input data-testid="chat-req-email" type="email" value={reqForm.email || ""} onChange={(e) => setReqForm({ ...reqForm, email: e.target.value })} placeholder="Email for a reply (optional)" className="w-full rounded-lg border border-[#E7E5E4] px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[#8A9A86]" />
              <div className="flex gap-2">
                <button data-testid="chat-req-submit" onClick={submitHuman} disabled={sending} className="flex-1 rounded-full bg-[#8A9A86] text-white py-2 text-sm font-medium hover:bg-[#77876F] transition-colors disabled:opacity-60">Connect me</button>
                <button onClick={() => setReqForm(null)} className="rounded-full border border-[#E7E5E4] px-4 py-2 text-sm text-[#44403C]">Cancel</button>
              </div>
            </div>
          )}

          {/* composer */}
          <div className="border-t border-[#E7E5E4] bg-white">
            {!humanMode && reqForm === null && (
              <button data-testid="chat-request-human" onClick={() => setReqForm({ name: convo?.guest_name || "", email: convo?.guest_email || "" })}
                className="w-full text-left px-4 py-2 text-xs text-[#8A9A86] hover:bg-[#F5F5F4] transition-colors inline-flex items-center gap-1.5 border-b border-[#F5F5F4]">
                <Headset className="w-3.5 h-3.5" /> Chat with Management instead
              </button>
            )}
            <div className="flex items-end gap-2 p-3">
              <textarea data-testid="chat-input" rows={1} value={input} onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
                placeholder="Type your message…" className="flex-1 resize-none max-h-24 rounded-xl border border-[#E7E5E4] px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[#8A9A86]" />
              <button data-testid="chat-send" onClick={send} disabled={sending || !input.trim()} aria-label="Send"
                className="w-10 h-10 rounded-full bg-[#1C1917] text-white flex items-center justify-center hover:bg-[#44403C] transition-colors disabled:opacity-50">
                {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" strokeWidth={1.75} />}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function Bubble({ m }) {
  if (m.role === "system") {
    return <p data-testid="chat-msg-system" className="text-center text-[11px] text-[#78716C] px-6 py-1">{m.text}</p>;
  }
  const mine = m.role === "guest";
  return (
    <div className={`flex ${mine ? "justify-end" : "justify-start"}`} data-testid={`chat-msg-${m.role}`}>
      <div className="max-w-[82%]">
        {!mine && <p className={`text-[10px] mb-0.5 font-medium ${m.role === "staff" ? "text-[#8A9A86]" : "text-[#78716C]"}`}>{m.author}</p>}
        <div className={`rounded-2xl px-3.5 py-2 text-sm leading-relaxed whitespace-pre-wrap ${mine ? "bg-[#1C1917] text-white rounded-br-sm" : m.role === "staff" ? "bg-[#E8EFE7] text-[#1C1917] rounded-bl-sm" : "bg-white border border-[#E7E5E4] text-[#1C1917] rounded-bl-sm"}`}>
          {m.text}
        </div>
      </div>
    </div>
  );
}
